import React from 'react';
import {
  autoUpdate,
  flip,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTypeahead
} from '@floating-ui/react';

/**
 * ============================================
 * ============================================
 * POPOVER SELECT HOOK
 * ============================================
 * ============================================
 * */
export interface PopoverSelectOptions {
  selectedLabel?: string | null;
  maxHeight?: number;
  updateSelectedLabel?: (label: string) => void;
}

export const usePopoverSelect = ({
  selectedLabel: selectedLabelProp,
  maxHeight: maxHeightProp,
  updateSelectedLabel
}: PopoverSelectOptions) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);
  const [selectedLabel, setSelectedLabel] = React.useState<string | null>(
    selectedLabelProp ?? null
  );

  /**
   * If you’re using the padding option in either middleware, ensure they share the same value.
   *
   * @see {@link https://floating-ui.com/docs/size#using-with-flip}
   * */
  const PADDING = 16;

  const { refs, floatingStyles, context } = useFloating({
    placement: 'bottom-start',
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      flip({ padding: PADDING }),
      size({
        padding: PADDING,
        apply({ availableHeight, elements }) {
          const maxHeight = maxHeightProp
            ? Math.min(maxHeightProp, availableHeight)
            : availableHeight;

          Object.assign(elements.floating.style, {
            maxHeight: `${maxHeight}px`
          });
        }
      }),
      offset(PADDING * 0.5)
    ]
  });

  const elementsRef = React.useRef<Array<HTMLElement | null>>([]);
  const labelsRef = React.useRef<Array<string | null>>([]);

  const handleSelect = React.useCallback((index: number | null) => {
    setSelectedIndex(index);
    setIsOpen(false);
    if (index !== null) {
      const updatedLabel = labelsRef.current[index] ?? '';

      setSelectedLabel(updatedLabel);
      updateSelectedLabel?.(updatedLabel);
    }
  }, []);

  function handleTypeaheadMatch(index: number | null) {
    if (isOpen) {
      setActiveIndex(index);
    } else {
      handleSelect(index);
    }
  }

  const listNav = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex
  });
  const typeahead = useTypeahead(context, {
    listRef: labelsRef,
    activeIndex,
    selectedIndex,
    onMatch: handleTypeaheadMatch
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'listbox' });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [listNav, typeahead, click, dismiss, role]
  );

  return React.useMemo(
    () => ({
      activeIndex,
      selectedIndex,
      getItemProps,
      handleSelect,
      getReferenceProps,
      refs,
      selectedLabel,
      getFloatingProps,
      floatingStyles,
      context,
      isOpen,
      elementsRef,
      labelsRef,
      setSelectedIndex,
      setActiveIndex,
      setSelectedLabel
    }),
    [
      activeIndex,
      selectedIndex,
      getItemProps,
      handleSelect,
      getReferenceProps,
      refs,
      selectedLabel,
      getFloatingProps,
      floatingStyles,
      context,
      isOpen
    ]
  );
};
